import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { ROUTES } from '../constants/constants';
import { FiCalendar } from 'react-icons/fi';
import { useUI } from '../context/UIContext';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';
import { useApp } from '../context/AppContext';
import Button from '../components/common/Button';
import { Icon, IconTile } from '../components/common/Icon';

const TYPE_ITEMS = [
  { icon: '🏗', name: 'Private Limited Co.', count: 'Companies Act, 2013 · 68 in portfolio', route: ROUTES.PVT_LIMITED },
  { icon: '🤝', name: 'LLP', count: 'LLP Act, 2008 · 24 in portfolio', route: ROUTES.LLP },
  { icon: '📈', name: 'Public / Listed Co.', count: 'SEBI + Companies Act · 8 in portfolio', route: ROUTES.PUBLIC_CO },
  { icon: '👤', name: 'OPC', count: 'One Person Co. · 12 in portfolio', route: ROUTES.OPC },
  { icon: '🌱', name: 'Section 8 / NGO', count: 'Non-profit · 9 in portfolio', route: ROUTES.NGO },
  { icon: '🏛', name: 'Partnership / AOP', count: 'Partnership Act · 6 in portfolio' },
  { icon: '🏦', name: 'NBFC', count: 'RBI regulated · 3 in portfolio' },
  { icon: '🏥', name: 'Healthcare / Hospital', count: 'Sector-specific compliances' },
  { icon: '🏫', name: 'Educational Institution', count: 'UGC / State Board norms' },
  { icon: '🏗', name: 'Real Estate / RERA', count: 'RERA Act 2016 compliances' },
  { icon: '💻', name: 'Startup (DPIIT)', count: 'Startup India Benefits & filings' },
  { icon: '🌍', name: 'Foreign Subsidiary', count: 'FEMA · RBI · ECB filings' },
];

const MATRIX = [
  ['Annual Return', '✅ MGT-7', '✅ Form 11', '✅ MGT-7A', '✅ MGT-7A', '✅ MGT-7', 'Annual', '₹100/day'],
  // AOC-4 vs AOC-4 XBRL is NOT an entity-type property. Rule 3 of the XBRL
  // Rules 2015 is an OR (listed / paid-up ≥ ₹5 cr / turnover ≥ ₹100 cr /
  // Ind-AS) that catches private companies too, so no column can claim XBRL
  // outright — the per-company determination lives in determineXbrl() and is
  // shown on the Annual Filing page. The footnote below carries the caveat.
  ['Financial Statements', '✅ AOC-4*', '✅ Form 8', '✅ AOC-4*', '✅ AOC-4*', '✅ AOC-4*', 'Annual', '₹100/day'],
  ['Director KYC', '✅ DIR-3', '✅ DPIN', '✅ DIR-3', '✅ DIR-3', '✅ DIR-3', 'Every 3rd FY', '₹5,000'],
  ['Board Meetings', '✅ 4/year', '—', '✅ 4/year', '✅ 2/year', '✅ 2/year', 'Quarterly', '₹25,000'],
  ['AGM', '✅ Yes', '—', '✅ Yes', '—', '✅ Yes', 'Annual', '₹1,00,000'],
  ['Statutory Audit', '✅ Yes', 'Turnover', '✅ Yes', '✅ Yes', '✅ Yes', 'Annual', '—'],
  ['GST Returns', '✅ GSTR-3B', '✅ GSTR-3B', '✅ GSTR-3B', '✅ GSTR-3B', 'If applicable', 'Monthly', '₹200/day'],
  ['TDS Filing', '✅ 24Q/26Q', '✅ Yes', '✅ Yes', '✅ Yes', '✅ Yes', 'Quarterly', '₹200/day'],
  ['SEBI Filing', '—', '—', '✅ BSE/NSE', '—', '—', 'Quarterly', '₹1 lakh+'],
  ['FCRA / RBI', 'If applicable', 'If applicable', 'If applicable', '—', '✅ FCRA', 'Annual', 'Varies'],
];

const CT_STATS_KEY = 'companyTypes:stats';

export default function CompanyTypesPage() {
  const navigate = useNavigate();
  const { showToast } = useUI();
  const { companies } = useApp();
  // Stale-while-revalidate: this route unmounts on nav, so the type cards
  // flashed a skeleton over stats just seen. Cache the stats payload and derive
  // the cards from it (services/viewCache); a revisit paints instantly.
  const [stats, setStats] = useState(() => getView(CT_STATS_KEY) ?? null);
  const [loading, setLoading] = useState(() => !hasView(CT_STATS_KEY));

  const fetchCompanyStats = useCallback(async () => {
    if (!hasView(CT_STATS_KEY)) setLoading(true);
    try {
      const response = await companiesAPI.getStats();
      if (response.success) {
        setStats(response.data);
        setView(CT_STATS_KEY, response.data);
      }
    } catch (error) {
      // Keep the cached stats on a failed refresh; toast only on a first load.
      console.error('Error fetching company stats:', error);
      if (!hasView(CT_STATS_KEY)) showToast('Failed to load company statistics', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Handle click on company type to navigate to filtered companies list
  const handleTypeClick = useCallback((type) => {
    // Navigate to companies page with type filter
    navigate(ROUTES.COMPANIES, { state: { typeFilter: type } });
  }, [navigate]);

  // Cards derive from stats, so a cached seed renders them instantly. With no
  // stats yet (true first load, or a failed first load) fall back to the static
  // list; a successful load narrows to the data-backed types with live counts.
  const typeItems = useMemo(() => {
    if (!stats) return TYPE_ITEMS;
    return [
      { icon: '🏗', name: 'Private Limited Co.', count: `Companies Act, 2013 · ${stats.pvtLtd || 0} in portfolio`, onClick: () => handleTypeClick('pvt') },
      { icon: '🤝', name: 'LLP', count: `LLP Act, 2008 · ${stats.llp || 0} in portfolio`, onClick: () => handleTypeClick('llp') },
      { icon: '📈', name: 'Public / Listed Co.', count: `SEBI + Companies Act · ${stats.public || 0} in portfolio`, onClick: () => handleTypeClick('public') },
      { icon: '👤', name: 'OPC', count: `One Person Co. · ${stats.opc || 0} in portfolio`, onClick: () => handleTypeClick('opc') },
      { icon: '🌱', name: 'Section 8 / NGO', count: `Non-profit · ${stats.sec8 || 0} in portfolio`, onClick: () => handleTypeClick('sec8') },
    ];
  }, [stats, handleTypeClick]);

  // Handle refresh statistics
  const handleRefresh = useCallback(async () => {
    await fetchCompanyStats();
    showToast('Company statistics refreshed', 'success');
  }, [fetchCompanyStats, showToast]);

  useEffect(() => {
    fetchCompanyStats();
  }, [fetchCompanyStats]);

  return (
    <AppLayout pageTitle="Company Types">
      <div className="text-[24px] font-bold mb-[5px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        All Company Types - Compliance Overview
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-[13px] text-[#7a7060]">
          Select a company type to view dedicated compliance checklist, key forms, deadlines & penalties
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} loading={loading}>
          Refresh Stats
        </Button>
      </div>

      {/* Summary Statistics */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-[22px]">
          <div className="bg-white rounded-[12px] p-4 border" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
            <div className="text-[10px] uppercase tracking-[1.5px] text-[#7a7060] font-semibold mb-2">Total Companies</div>
            <div className="text-[28px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#3a4a66' }}>{stats.total}</div>
          </div>
          <div className="bg-white rounded-[12px] p-4 border" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
            <div className="text-[10px] uppercase tracking-[1.5px] text-[#7a7060] font-semibold mb-2">Active</div>
            <div className="text-[28px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#4a6b3f' }}>{stats.active}</div>
          </div>
          <div className="bg-white rounded-[12px] p-4 border" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
            <div className="text-[10px] uppercase tracking-[1.5px] text-[#7a7060] font-semibold mb-2">Inactive</div>
            <div className="text-[28px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#8a6a1f' }}>{stats.inactive}</div>
          </div>
          <div className="bg-white rounded-[12px] p-4 border" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
            <div className="text-[10px] uppercase tracking-[1.5px] text-[#7a7060] font-semibold mb-2">Avg Score</div>
            <div className="text-[28px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#145f5f' }}>{stats.avgScore ? Math.round(stats.avgScore) : 0}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-[22px]">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 12 }).map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="text-center p-2 py-3 rounded-[11px] border animate-pulse"
              style={{ background: '#f8f5ef', borderColor: '#ddd6c6' }}
            >
              <div className="w-[26px] h-[26px] bg-gray-300 rounded mb-2 mx-auto"></div>
              <div className="h-[12px] bg-gray-300 rounded mb-[3px] w-[90%] mx-auto"></div>
              <div className="h-[10px] bg-gray-300 rounded w-[80%] mx-auto"></div>
            </div>
          ))
        ) : (
          typeItems.map((item) => (
            <button
              key={item.name}
              onClick={item.onClick || (() => item.route && navigate(item.route))}
              className="group text-center p-2 py-3 rounded-[11px] border transition-all hover:-translate-y-[2px] hover:shadow-md"
              style={{ background: '#fff', borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}
            >
              <IconTile name={item.icon} size="lg" interactive className="mb-2 mx-auto" />
              <div className="text-[12.5px] font-bold mb-[3px]">{item.name}</div>
              <div className="text-[10px] text-[#7a7060]">{item.count}</div>
            </button>
          ))
        )}
      </div>

      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
        <div className="p-4 border-b flex items-center gap-2" style={{ borderColor: '#ddd6c6' }}>
          <div className="flex-1 text-[16px] font-bold flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Icon name="documents" size={17} style={{ color: '#145f5f' }} />
            Universal Compliance Matrix
          </div>
          <Button variant="outline" size="sm" leftIcon={<FiCalendar size={15} />} onClick={() => navigate(ROUTES.CALENDAR)}>
            View Calendar
          </Button>
        </div>

        <div className="p-4 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Compliance</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Pvt Ltd</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">LLP</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Public</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">OPC</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Sec 8</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Frequency</th>
                <th className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060]">Penalty</th>
              </tr>
            </thead>
            <tbody>
              {MATRIX.map((row) => (
                <tr key={row[0]} style={{ borderBottom: '1px solid #ddd6c6' }}>
                  {row.map((cell, index) => (
                    <td key={`${row[0]}-${index}`} className="px-[14px] py-[11px] text-[12.5px]">
                      {index === 0 ? <strong>{cell}</strong> : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Scope + XBRL caveats. The matrix is a recurring-obligation view;
              anything event-triggered has no place in a frequency column. */}
          <div className="mt-[14px] rounded-[8px] p-[13px] text-[11.5px] leading-[1.55] text-[#7a7060] flex flex-col gap-[7px]" style={{ background: '#f8f5ef' }}>
            <div className="flex gap-2">
              <Icon name="info" size={14} style={{ color: '#8a7d66', flexShrink: 0, marginTop: 2 }} />
              <span>
                <strong>* AOC-4 or AOC-4 XBRL.</strong> XBRL is not decided by company type. Under Rule 3 of the
                XBRL Rules, 2015 it applies if <em>any one</em> of these is true &mdash; the company is listed (or is an
                Indian subsidiary of a listed company), paid-up capital is &#8377;5 crore or more, turnover is
                &#8377;100 crore or more, or accounts are prepared under Ind-AS &mdash; and once a company has filed in
                XBRL it must continue to, even if it later falls below every threshold. So a public company does not
                automatically file in XBRL, and a private company with paid-up capital of &#8377;5 crore or more does.
                Open a company&rsquo;s Annual Filing page for its computed position, the basis for it, and a manual override.
              </span>
            </div>
            <div className="flex gap-2">
              <Icon name="info" size={14} style={{ color: '#8a7d66', flexShrink: 0, marginTop: 2 }} />
              <span>
                This matrix covers <strong>recurring</strong> obligations only. Event-based filings are not shown &mdash;
                for example <strong>DIR-12</strong> (Sec 170) is due within 30 days of a director or KMP appointment,
                cessation or change in designation, not on an annual cycle.
              </span>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
